// Okna akcí: nová akce, úprava základních údajů, změna stavu (fáze 3a).
// Změna stavu se vždy potvrzuje a zapíše do časové osy firmy.
import React, { useEffect, useState } from "react";
import { CalendarCheck, ArrowRightLeft, Search, Plus, Trash2 } from "lucide-react";
import { Modal, HlavickaOkna, Field, Hlaska, RozbalovaciVyber } from "../ui.jsx";
import DatePicker from "../DatePicker.jsx";
import { toast } from "../Toast.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import { b2bGet, b2bPost } from "./b2bApi.js";
import { STAVY_AKCE, kc } from "./b2bUi.jsx";

const CASY = (() => { const o = [{ key: "", label: "neurčeno" }]; for (let m = 6 * 60; m <= 22 * 60; m += 15) { const c = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; o.push({ key: c, label: c }); } return o; })();
const PRAZDNA = { firma_id: "", osoba_id: "", produkt_id: "", nazev: "", datum: "", cas_od: "", cas_do: "", misto: "", pocet_lidi: "", cena_bez_dph: "", dph_sazba: "21", poznamka: "" };

/** Hledání firmy při zakládání akce z přehledu akcí. */
function VyberFirmy({ firma, onVyber }) {
  const [q, setQ] = useState("");
  const [vysl, setVysl] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setVysl([]); return undefined; }
    const t = setTimeout(() => { b2bGet("firmy", { q }).then((r) => setVysl((r.firmy || []).slice(0, 6))).catch(() => setVysl([])); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (firma) {
    return (
      <div className="flex min-h-[44px] items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2">
        <span className="min-w-0 flex-1 text-sm font-bold text-slate-900">{firma.nazev}</span>
        <button type="button" className="btn-secondary btn-sm" onClick={() => onVyber(null)}>Změnit</button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="h-4 w-4" /></span>
        <input className="field !pl-9" placeholder="Hledej firmu podle názvu" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Hledat firmu" />
      </div>
      {vysl.map((f) => (
        <button key={f.id} type="button" className="flex min-h-[44px] w-full items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-900" onClick={() => onVyber(f)}>{f.nazev}</button>
      ))}
      {q.trim().length >= 2 && vysl.length === 0 && <p className="text-sm text-slate-500">Žádná firma nenalezena. Nejdřív ji založ v sekci Firmy.</p>}
    </div>
  );
}

export function AkceDialog({ open, akce, firma: firmaVstup, osoby = [], onClose, onHotovo }) {
  const [a, setA] = useState(PRAZDNA);
  const [firma, setFirma] = useState(firmaVstup || null);
  const [produkty, setProdukty] = useState([]);
  const [osobyF, setOsobyF] = useState(osoby);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const uprava = !!akce;
  useEffect(() => {
    if (!open) return;
    setChyba("");
    setFirma(firmaVstup || null);
    setOsobyF(osoby);
    b2bGet("produkty", { aktivni: 1 }).then((r) => setProdukty(r.produkty || [])).catch(() => {});
    setA(akce ? {
      ...PRAZDNA, ...Object.fromEntries(Object.keys(PRAZDNA).map((k) => [k, akce[k] ?? ""])), osoba_id: akce.osoba_id ?? "", produkt_id: akce.produkt_id ?? "",
      pocet_lidi: akce.pocet_lidi ?? "", cena_bez_dph: akce.cena_bez_dph ?? "", dph_sazba: String(akce.dph_sazba ?? 21),
    } : { ...PRAZDNA, firma_id: firmaVstup?.id || "" });
  }, [open, akce, firmaVstup]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !firma || firmaVstup) return;
    b2bGet("firma", { id: firma.id }).then((r) => setOsobyF(r.osoby || [])).catch(() => setOsobyF([]));
  }, [open, firma, firmaVstup]);
  const set = (k) => (e) => setA((p) => ({ ...p, [k]: e.target.value }));
  const vyberProduktu = (id) => {
    const p = produkty.find((x) => String(x.id) === String(id));
    setA((x) => ({
      ...x, produkt_id: id,
      nazev: !x.nazev.trim() && p ? p.nazev : x.nazev,
      cena_bez_dph: (x.cena_bez_dph === "" || x.cena_bez_dph == null) && p && p.cena_od != null && p.cena_od === p.cena_do && p.jednotka === "akce" ? p.cena_od : x.cena_bez_dph,
    }));
  };
  const uloz = async () => {
    setBusy(true); setChyba("");
    try {
      const telo = { ...a, produkt_id: a.produkt_id || null, osoba_id: a.osoba_id || null, datum: a.datum || null };
      const r = uprava ? await b2bPost("akce_upravit", { id: akce.id, ...telo }) : await b2bPost("akce_zalozit", { ...telo, firma_id: firma?.id });
      toast(uprava ? "Akce uložena." : "Akce založena.", "success");
      onHotovo?.(r.akce);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const mozno = uprava || !!firma;
  return (
    <Modal open={open} onClose={onClose} size="lg" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarCheck} nazev={uprava ? "Upravit akci" : "Nová akce"} popis={uprava ? akce.nazev : firma?.nazev} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !mozno || !(a.nazev.trim() || a.produkt_id)} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        {!uprava && !firmaVstup && <div><span className="label">Firma</span><VyberFirmy firma={firma} onVyber={setFirma} /></div>}
        <RozbalovaciVyber stitek="Produkt" aria-label="Produkt" value={String(a.produkt_id ?? "")} onChange={vyberProduktu}
          items={[{ key: "", label: "Bez produktu" }, ...produkty.map((p) => ({ key: String(p.id), label: p.nazev }))]} />
        <Field label="Název akce"><input className="field" value={a.nazev} onChange={set("nazev")} placeholder="například Den zdraví pro zaměstnance" /></Field>
        {osobyF.length > 0 && (
          <RozbalovaciVyber stitek="Kontaktní osoba" aria-label="Kontaktní osoba" value={String(a.osoba_id ?? "")} onChange={(v) => setA((p) => ({ ...p, osoba_id: v }))}
            items={[{ key: "", label: "Neurčena" }, ...osobyF.map((o) => ({ key: String(o.id), label: o.jmeno }))]} />
        )}
        <div>
          <span className="label">Datum</span>
          <DatePicker value={a.datum || ""} onChange={(v) => setA((p) => ({ ...p, datum: v }))} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <RozbalovaciVyber stitek="Od" aria-label="Začátek" value={a.cas_od || ""} onChange={(v) => setA((p) => ({ ...p, cas_od: v }))} items={CASY} />
          <RozbalovaciVyber stitek="Do" aria-label="Konec" value={a.cas_do || ""} onChange={(v) => setA((p) => ({ ...p, cas_do: v }))} items={CASY} />
        </div>
        <Field label="Místo"><input className="field" value={a.misto || ""} onChange={set("misto")} placeholder="adresa nebo budova" /></Field>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Počet lidí"><input className="field" inputMode="numeric" value={a.pocet_lidi} onChange={set("pocet_lidi")} /></Field>
          <Field label="Cena bez DPH, Kč"><input className="field" inputMode="decimal" value={a.cena_bez_dph} onChange={set("cena_bez_dph")} disabled={uprava && !!akce.fapi_invoice_id} /></Field>
          <Field label="DPH, %"><input className="field" inputMode="decimal" value={a.dph_sazba} onChange={set("dph_sazba")} disabled={uprava && !!akce.fapi_invoice_id} /></Field>
        </div>
        <Field label="Poznámka"><textarea className="field" rows={2} value={a.poznamka || ""} onChange={set("poznamka")} /></Field>
      </div>
    </Modal>
  );
}

/* --- Změna stavu ------------------------------------------------------------ */
const RUCNE = ["nabidka", "potvrzeno", "realizovano", "zruseno"];
export function StavAkceDialog({ open, akce, firmaStav, onClose, onHotovo }) {
  const [stav, setStav] = useState("");
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setStav(""); setChyba(""); } }, [open]);
  if (!akce) return null;
  const uloz = async () => {
    const klient = stav === "potvrzeno" && !["klient", "nekontaktovat"].includes(firmaStav);
    const ok = await confirmDialog({
      title: "Změnit stav akce?",
      description: `Akce ${akce.nazev} přejde na stav ${STAVY_AKCE[stav].label}.${klient ? " Firma se tím stane klientem." : ""} Změna se zapíše do časové osy.`,
      confirmLabel: "Změnit stav", variant: stav === "zruseno" ? "danger" : "primary",
    });
    if (!ok) return;
    setBusy(true); setChyba("");
    try {
      const r = await b2bPost("akce_stav", { id: akce.id, stav });
      toast(r.firma_klient ? "Stav změněn, firma je teď klient." : "Stav změněn.", "success");
      onHotovo?.(r);
    } catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  const zamcena = ["vyfakturovano", "uhrazeno"].includes(akce.stav) || !!akce.fapi_invoice_id;
  return (
    <Modal open={open} onClose={onClose} size="sm" plnaHlavicka
      header={<HlavickaOkna ikona={ArrowRightLeft} nazev="Změnit stav akce" popis={akce.nazev} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy || !stav || zamcena} aria-busy={busy}>Pokračovat</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        {zamcena && <Hlaska ton="pozor">Akce už má fakturu, stav se řídí fakturou.</Hlaska>}
        <RozbalovaciVyber stitek="Nový stav" aria-label="Nový stav" value={stav} onChange={setStav}
          items={RUCNE.filter((k) => k !== akce.stav).map((k) => ({ key: k, label: STAVY_AKCE[k].label }))} />
        <p className="text-sm text-slate-600">Stav Vyfakturováno a Uhrazeno nastaví faktura.</p>
      </div>
    </Modal>
  );
}

/* --- Harmonogram a náklady (v detailu akce) ---------------------------------- */
const mi = (c) => { const [h, m] = String(c || "").split(":").map(Number); return h * 60 + m; };
export function kapacitaBloku(s) {
  const n = Number(s.min_na_osobu) || 0;
  if (!n || !s.od || !s.do) return null;
  const volne = mi(s.do) - mi(s.od) - (Number(s.prestavka_min) || 0);
  return volne <= 0 ? 0 : Math.floor(volne / n) * Math.max(1, Number(s.mist) || 1);
}

export function HarmonogramDialog({ open, akce, lektori = [], onClose, onHotovo }) {
  const [h, setH] = useState([]);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open && akce) { setChyba(""); setH((akce.harmonogram || []).map((s) => ({ od: s.od || "", do: s.do || "", co: s.co || "", kdo: s.kdo || "", mist: s.mist || 1, min_na_osobu: s.min_na_osobu || "", prestavka_min: s.prestavka_min || "" }))); } }, [open, akce]);
  if (!akce) return null;
  const zmen = (i, k, v) => setH((p) => p.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const uloz = async () => {
    setBusy(true); setChyba("");
    try { const r = await b2bPost("akce_upravit", { id: akce.id, harmonogram: h }); toast("Harmonogram uložen.", "success"); onHotovo?.(r.akce); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="lg" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarCheck} nazev="Harmonogram" popis={akce.nazev} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary mr-auto" onClick={() => setH((p) => [...p, { od: "", do: "", co: "", kdo: "", mist: 1, min_na_osobu: "", prestavka_min: "" }])}><Plus className="h-4 w-4" />Přidat blok</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        {h.length === 0 && <p className="text-sm text-slate-500">Harmonogram je prázdný. Přidej první blok, například přednášku nebo měření InBody.</p>}
        {h.map((s, i) => {
          const kap = kapacitaBloku(s);
          return (
            <div key={i} className="polozka space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <RozbalovaciVyber stitek="Od" aria-label="Začátek bloku" value={s.od} onChange={(v) => zmen(i, "od", v)} items={CASY} />
                <RozbalovaciVyber stitek="Do" aria-label="Konec bloku" value={s.do} onChange={(v) => zmen(i, "do", v)} items={CASY} />
              </div>
              <Field label="Co se děje"><input className="field" value={s.co} onChange={(e) => zmen(i, "co", e.target.value)} placeholder="například Měření InBody" /></Field>
              <Field label="Kdo">
                <input className="field" list="lektori-moznosti" value={s.kdo} onChange={(e) => zmen(i, "kdo", e.target.value)} />
              </Field>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Měřicích míst"><input className="field" inputMode="numeric" value={s.mist} onChange={(e) => zmen(i, "mist", e.target.value)} /></Field>
                <Field label="Minut na osobu"><input className="field" inputMode="numeric" value={s.min_na_osobu} onChange={(e) => zmen(i, "min_na_osobu", e.target.value)} /></Field>
                <Field label="Přestávky, minut"><input className="field" inputMode="numeric" value={s.prestavka_min} onChange={(e) => zmen(i, "prestavka_min", e.target.value)} /></Field>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex-1 text-sm text-slate-600">{kap === null ? "Kapacita se počítá, když vyplníš minuty na osobu." : `Kapacita bloku: ${kap} lidí`}</span>
                <button type="button" className="btn-ikona" aria-label="Odebrat blok" title="Odebrat" onClick={() => setH((p) => p.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
        <datalist id="lektori-moznosti">{lektori.map((l) => <option key={l} value={l} />)}</datalist>
      </div>
    </Modal>
  );
}

export function NakladyDialog({ open, akce, onClose, onHotovo }) {
  const [n, setN] = useState([]);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open && akce) { setChyba(""); setN((akce.naklady || []).map((x) => ({ nazev: x.nazev, castka: x.castka }))); } }, [open, akce]);
  if (!akce) return null;
  const soucet = n.reduce((s, x) => s + (Number(String(x.castka).replace(/\s/g, "").replace(",", ".")) || 0), 0);
  const uloz = async () => {
    setBusy(true); setChyba("");
    try { const r = await b2bPost("akce_upravit", { id: akce.id, naklady: n }); toast("Náklady uloženy.", "success"); onHotovo?.(r.akce); }
    catch (e) { setChyba(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={CalendarCheck} nazev="Náklady" popis={akce.nazev} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary mr-auto" onClick={() => setN((p) => [...p, { nazev: "", castka: "" }])}><Plus className="h-4 w-4" />Přidat položku</button>
        <button type="button" className="btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={busy} aria-busy={busy}>Uložit</button>
      </>}>
      <div className="space-y-3">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        {n.length === 0 && <p className="text-sm text-slate-500">Zatím žádné náklady. Přidej třeba lektora, dopravu nebo materiál.</p>}
        {n.map((x, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="min-w-0 flex-1"><Field label="Položka"><input className="field" value={x.nazev} onChange={(e) => setN((p) => p.map((y, j) => (j === i ? { ...y, nazev: e.target.value } : y)))} /></Field></div>
            <div className="w-32 shrink-0"><Field label="Částka, Kč"><input className="field" inputMode="decimal" value={x.castka} onChange={(e) => setN((p) => p.map((y, j) => (j === i ? { ...y, castka: e.target.value } : y)))} /></Field></div>
            <button type="button" className="btn-ikona shrink-0" aria-label="Odebrat položku" title="Odebrat" onClick={() => setN((p) => p.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="text-right text-sm font-semibold text-slate-700">Celkem {kc(soucet)}</div>
      </div>
    </Modal>
  );
}
